import React, { useState,useEffect } from "react";
import { 
  Dialog, DialogTitle, DialogContent, DialogActions, 
  Button, Typography, Autocomplete, TextField, Checkbox,
  Box, Paper, Divider, IconButton
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import WorkIcon from "@mui/icons-material/Work";
import axios from "axios";
import { toast } from "react-toastify";
import dayjs from 'dayjs';

const ClearPopup = ({ fetchData, open, onClose, monthData, shiftData, Authorization, propId, YearNm, MonthNm, shiftId ,selectedMonthProp, selectedShiftsProp  }) => {
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedShifts, setSelectedShifts] = useState([]);
  const [monthError, setMonthError] = useState(false);
  const [shiftError, setShiftError] = useState(false);
  const userId = sessionStorage.getItem('UserId');

    useEffect(() => {
    if (open) {
      if (selectedMonthProp) {
        setSelectedMonth(selectedMonthProp);
      }
      if (selectedShiftsProp && selectedShiftsProp.length > 0) {
        // Convert shift IDs to full shift objects
        const shiftObjects = shiftData.filter(shift => 
          selectedShiftsProp.includes(shift.id)
        );
        setSelectedShifts(shiftObjects);
      }
    }
  }, [open, selectedMonthProp, selectedShiftsProp, shiftData]);

  const handleMonthChange = (_, newValue) => {
    setSelectedMonth(newValue);
    setMonthError(false);
  };
  

  const handleShiftChange = (_, newValue) => {
    setSelectedShifts(newValue);
    setShiftError(false);
  };

  const validateFields = () => {
    let isValid = true;
    if (!selectedMonth) {
      setMonthError(true);
      isValid = false;
    }
    if (selectedShifts.length === 0) {
      setShiftError(true);
      isValid = false;
    }
    return isValid;
  };
  
  const handleClose = () => {
    setSelectedMonth(null);  
    setSelectedShifts([]);   
    setMonthError(false);    
    setShiftError(false);
    onClose();           
  };
  
  const handleClear = async () => {
    if (!validateFields()) {
      toast.error("Please select both Month and Shifts before clearing.");
      return;
    }
  
    const shiftIds = selectedShifts.map(shift => shift.id).join(","); 
  
    const payload = {
      PropId: propId,
      MonthNm: MonthNm, 
      YearNm: YearNm,   
      ShiftId: shiftIds 
    };
  
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}HkTargetCalendar/DeleteHkTargetCalendar`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${Authorization}`,
            "Content-Type": "application/json",
          },
        }
      );
  
      if (response.data.Status === 0) {
        toast.success(response.data.Message || "Data cleared successfully");
        fetchData();
        handleClose(); 
      } else {
        toast.error(response.data.Message || "Failed to clear data");
      }
    } catch (error) {
      toast.error("Error clearing data: " + (error.response?.data?.message || error.message));
    }
  };
  
  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
        }
      }}
    >
      <DialogTitle sx={{ 
        backgroundColor: '#f44336', 
        color: 'white', 
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: 'center',
        py: 1
      }}>
        <Box display="flex" alignItems="center">
          <DeleteIcon sx={{ mr: 1 }} />
          <Typography variant="h6" fontWeight="600">Clear Data</Typography>
        </Box>
        <IconButton onClick={handleClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Current Period: {MonthNm} {YearNm}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <CalendarMonthIcon sx={{ color: '#f44336', mr: 1 }} />
          <Typography variant="subtitle1" fontWeight="500">
            Select Month
          </Typography>
        </Box>
        
        <Autocomplete
          options={monthData}
          getOptionLabel={(option) => option.name}
          value={selectedMonth}
          onChange={handleMonthChange}
          renderInput={(params) => (
            <TextField 
              {...params} 
              label="Month to Clear" 
              variant="outlined" 
              fullWidth 
              required
              error={monthError}
              helperText={monthError ? "Month is required" : ""}
            />
          )}
          sx={{ mb: 1 }}
          size="small"
        />

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <WorkIcon sx={{ color: '#f44336', mr: 1 }} />
          <Typography variant="subtitle1" fontWeight="500">
            Select Shifts to Clear
          </Typography>
        </Box>
        
        <Autocomplete
          multiple
          options={shiftData}
          disableCloseOnSelect={false}
          getOptionLabel={(option) => option.name}
          value={selectedShifts}
          onChange={handleShiftChange}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox 
                style={{ marginRight: 8 }} 
                checked={selected}
                sx={{ color: '#f44336', '&.Mui-checked': { color: '#f44336' } }}
              />
              {option.name}
            </li>
          )}
          renderInput={(params) => (
            <TextField 
              {...params} 
              label="Shifts to Clear" 
              variant="outlined" 
              fullWidth 
              required
              error={shiftError}
              helperText={shiftError ? "At least one shift must be selected" : ""}
            />
          )}
          sx={{ mb: 1 }}
          size="small"
        />

        {(selectedMonth || selectedShifts.length > 0) && (
          <Paper elevation={0} sx={{ 
            backgroundColor: "#fff8f7", 
            p: 2, 
            borderRadius: 2, 
            border: '1px solid #ffcdd2'
          }}>
            <Typography variant="subtitle2" color="error" gutterBottom>
              Warning: Data will be permanently deleted
            </Typography>
            <Divider sx={{ mb: 1.5, borderColor: '#ffcdd2' }} />
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <Box component="span" fontWeight="500">Month:</Box> {selectedMonth?.name || "None"}
            </Typography>
            <Typography variant="body2">
              <Box component="span" fontWeight="500">Shifts to clear:</Box> {selectedShifts.length ? selectedShifts.map(shift => shift.name).join(", ") : "None"}
            </Typography>
          </Paper>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 1, backgroundColor: '#fff8f7' }}>
      <Button 
          startIcon={<DeleteIcon />} 
          onClick={handleClear} 
          variant="contained"
          color="error"
          sx={{ 
            borderRadius: 1.5,
            textTransform: 'none',
            fontWeight: 500,
            '&:hover': {
              backgroundColor: '#d32f2f'
            },
            boxShadow: '0 2px 6px rgba(244, 67, 54, 0.25)'
          }}
        >
          Delete 
        </Button>
        <Button 
          onClick={handleClose} 
          variant="outlined"
          color="inherit"
          sx={{ 
            borderRadius: 1.5,
            textTransform: 'none',
            fontWeight: 500
          }}
        >
          Cancel
        </Button>
     
      </DialogActions>
    </Dialog>
  );
};

export default ClearPopup;